// The framework's own appearance. Importing it here means every app that uses the
// framework gets styled screens without copying rules into its own stylesheet.
import './framework.css';

/**
 * ============================================================================
 *  FRAMEWORK · REACT — public API. The only path an app should import from.
 * ============================================================================
 *
 *  Nothing under framework/ knows this site exists. It is generic over whatever
 *  sections an application declares, which is what makes it reusable.
 *
 *  An app binds its own section types ONCE:
 *
 *      // app/web/cms.ts
 *      export const { cms, SectionUpdate } = createCms<SectionTypes>();
 *
 *  after which `cms.get(SectionName.Hero)` returns a `HeroSection` with no casts
 *  and no generic arguments at the call site.
 */
import type { ReactNode } from 'react';
import { ALL_LANGS, langLabel, isPermissionNotGranted, CACHE_TTL_MS } from '@framework/core';

import { useSection } from './data/use-section';
import { getLang, setLang, useLang } from './lang/lang-store';
import { flushLocal, useFlushLocal, flushServer, useFlushServer } from './data/flush';
import {
  SectionUpdate as RawSectionUpdate, type EditHandle, type PencilAt,
} from './editing/SectionUpdate';
export type { Focus } from './editing/DialogForm';
import type { Focus } from './editing/DialogForm';
import type { SectionHandle } from './data/types';

/* -------------------------------------------------------------------------- */
/*  Typed binding                                                              */
/* -------------------------------------------------------------------------- */

export interface SectionUpdateProps<TMap, K extends keyof TMap & string> {
  name: K;
  /**
   * `(content, edit) => …` — the second argument is optional and lets a view place
   * its own edit trigger anywhere, instead of the default corner pencil.
   */
  children: (data: TMap[K], edit: EditHandle) => ReactNode;
  /** Where the built-in pencil sits. Default 'top-right'; 'none' to place your own. */
  pencil?: PencilAt;
  /** Live preview as the editor types, before saving. */
  preview?: (data: Record<string, unknown>) => void;
  /** Rendered when this session may not view the section. Defaults to nothing. */
  fallback?: ReactNode;
  /** Narrow the editor to one row of one list field, for a page that is about that row. */
  focus?: Focus;
}

export interface TypedCms<TMap> {
  get<K extends keyof TMap & string>(name: K): SectionHandle<TMap[K]>;
  get<K extends keyof TMap & string>(names: readonly K[]): { [P in K]: SectionHandle<TMap[P]> };
  getLang: typeof getLang;
  setLang: typeof setLang;
  useLang: typeof useLang;
  /** THIS browser only. Always allowed — no server call, so it cannot be refused. */
  flushLocal: typeof flushLocal;
  useFlushLocal: typeof useFlushLocal;
  /** The shared epoch — every visitor. Admin only; the server answers 403 otherwise. */
  flushServer: typeof flushServer;
  useFlushServer: typeof useFlushServer;
  isPermissionNotGranted: typeof isPermissionNotGranted;
  langs: typeof ALL_LANGS;
  langLabel: typeof langLabel;
  ttlMs: number;
}

/**
 * Binds an app's section map to the framework. Pure typing — the runtime objects are
 * module-level singletons, so calling this more than once is harmless.
 */
export function createCms<TMap extends object>(): {
  cms: TypedCms<TMap>;
  SectionUpdate: <K extends keyof TMap & string>(p: SectionUpdateProps<TMap, K>) => ReactNode;
} {
  const cms = {
    get: useSection,
    getLang, setLang, useLang, flushLocal, useFlushLocal, flushServer, useFlushServer,
    isPermissionNotGranted,
    langs: ALL_LANGS,
    langLabel,
    ttlMs: CACHE_TTL_MS,
  } as unknown as TypedCms<TMap>;

  const SectionUpdate = RawSectionUpdate as unknown as
    <K extends keyof TMap & string>(p: SectionUpdateProps<TMap, K>) => ReactNode;

  return { cms, SectionUpdate };
}

/* -------------------------------------------------------------------------- */
/*  Re-exports, grouped by responsibility                                      */
/* -------------------------------------------------------------------------- */

// data
export { useSaveSection, type SaveVars } from './data/use-save';
export { useSectionHistory } from './data/use-history';
export { clearCapabilities } from './data/capability-store';
export { setEpochHandler } from './data/section-loader';
export { noteEpoch } from './data/flush';
export {
  isPersistableKey, isPersistableEntry, sectionKey, historyKey, PERSISTED_CACHE_KEY,
} from './data/cache-keys';
export type { SectionHandle, SectionMap, ServerFlush } from './data/types';

// auth
export { AuthProvider, useAuth, type Session } from './auth/AuthProvider';

// theme
export { applyCssVars, bootstrapCssVars, type CssVarMap } from './theme/css-vars';

// markdown — @Paragraph({ markdown: true }). The app supplies the directive vocabulary.
// A page renders the value a @RenderOf field already holds. That is all a site needs,
// and it costs nothing: <Html> is a div.
export { Html, type HtmlProps } from './markdown/Html';
// <Markdown> is deliberately NOT re-exported here. Importing it pulls the whole markdown
// parser into whatever bundle does so, and the point of rendering on save is that the
// browser never carries it. The server deep-imports it; anything else must do so on
// purpose, and lazily.

// location
export { useHash, useActiveSection, useMountedSections, registerMounted } from './location/index';

// flags
export {
  registerFlag, allFlags, flagDefaults, isKnownFlag, flagByKey, flagByCode,
  freeCodes, MAX_FLAGS, type FlagDef,
} from './flags/registry';
export {
  useFlag, useFlags, setFlag, resetFlags, flagEnabled, useFlagRows,
  getFlagCode, useFlagCode, enableFlagCode,
} from './flags/store';
export {
  encodeFlags, decodeFlags, describeCode, isValidCode, codeFor,
} from './flags/flag-code';
export { FLAG_ALPHABET, describeAlphabet } from './flags/alphabet';
export { applyFlagsFromUrl, watchFlagsInUrl, FLAG_PARAM } from './flags/url-flags';
export {
  FLAG_ACCOUNT_BAR, FLAG_DEV_BAR, FLAG_SECTION_OUTLINES, FLAG_BATCH_LOG,
} from './flags/built-in';

// errors — a client crash leaves no trace on its own; these are what give it one
export { ErrorBoundary } from './errors/ErrorBoundary';
export { installClientErrorReporting, reportClientError } from './errors/report';

// http
export { api, get as httpGet, post, put, cx, onKey, isApiFailure, type ApiFailure } from './http/client';

// editing
export { customRenderers } from './editing/renderers/index';
export type { EditHandle, PencilAt };

// screens — every one reachable by URL
export { SCREENS, isScreen, goTo, type Screen } from './screens/routes';
export { AccountBar } from './screens/AccountBar';
export { LoginScreen } from './screens/LoginScreen';
export { AdminScreen } from './screens/AdminScreen';
export { AdminUsers, type AdminUser } from './screens/AdminUsers';
export { LangPicker } from './screens/LangPicker';
export { Profile } from './screens/Profile';
export { SecretFlags } from './screens/SecretFlags';
export { FlushLocal } from './screens/FlushLocal';
export { FlushServer } from './screens/FlushServer';
// No Deploy screen: deploying lives on the deploy server, served as a standalone
// page. It is not part of the website.
